import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { addMonths, addYears, addWeeks } from 'date-fns';
import { INITIAL_DEMO_SUBSCRIPTIONS } from '../lib/demoData';

const SubscriptionContext = createContext();

export const useSubscriptions = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptions must be used within a SubscriptionProvider');
  }
  return context;
};

const DEMO_SUBS_KEY = 'spend_easy_demo_subscriptions';

const loadDemoSubs = () => {
  try {
    const saved = localStorage.getItem(DEMO_SUBS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.map(s => ({
        ...s,
        startDate: new Date(s.startDate),
        nextRenewalDate: new Date(s.nextRenewalDate),
        createdAt: s.createdAt ? new Date(s.createdAt) : new Date()
      }));
    }
  } catch (e) {
    console.error('Error loading demo subscriptions:', e);
  }
  return INITIAL_DEMO_SUBSCRIPTIONS;
};

const saveDemoSubs = (s) => {
  try {
    localStorage.setItem(DEMO_SUBS_KEY, JSON.stringify(s));
  } catch (e) {
    console.error('Error saving demo subscriptions:', e);
  }
};

export const SubscriptionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setSubscriptions([]);
      setLoading(false);
      return;
    }

    if (currentUser.isGuest) {
      const updateGuestData = () => {
        setSubscriptions(loadDemoSubs());
      };
      updateGuestData();
      setLoading(false);
      window.addEventListener('spend_easy_demo_data_updated', updateGuestData);
      return () => {
        window.removeEventListener('spend_easy_demo_data_updated', updateGuestData);
      };
    }

    const subsRef = collection(db, 'users', currentUser.uid, 'subscriptions');
    const q = query(subsRef, orderBy('nextRenewalDate', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const subsList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        startDate: docSnap.data().startDate?.toDate?.() || new Date(),
        nextRenewalDate: docSnap.data().nextRenewalDate?.toDate?.() || new Date(),
        createdAt: docSnap.data().createdAt?.toDate?.() || new Date(),
      }));
      setSubscriptions(subsList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching subscriptions:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const calculateNextRenewal = (startDate, cycle) => {
    const start = new Date(startDate || new Date());
    let nextDate = new Date(start);
    const today = new Date();
    let iterations = 0;
    while (nextDate <= today && iterations < 1000) {
      iterations++;
      if (cycle === 'monthly') nextDate = addMonths(nextDate, 1);
      else if (cycle === 'yearly') nextDate = addYears(nextDate, 1);
      else if (cycle === 'weekly') nextDate = addWeeks(nextDate, 1);
      else nextDate = addMonths(nextDate, 1);
    }
    return nextDate;
  };

  const addSubscription = async (subData) => {
    if (!currentUser) throw new Error('No user logged in');
    const nextRenewal = calculateNextRenewal(subData.startDate, subData.billingCycle);

    if (currentUser.isGuest) {
      const newSub = {
        ...subData,
        id: `demo-sub-${Date.now()}`,
        userId: currentUser.uid,
        startDate: new Date(subData.startDate),
        nextRenewalDate: nextRenewal,
        isActive: true,
        createdAt: new Date()
      };
      const updated = [...subscriptions, newSub];
      setSubscriptions(updated);
      saveDemoSubs(updated);
      return { id: newSub.id };
    }

    const subsRef = collection(db, 'users', currentUser.uid, 'subscriptions');
    return addDoc(subsRef, {
      ...subData,
      userId: currentUser.uid,
      startDate: Timestamp.fromDate(new Date(subData.startDate)),
      nextRenewalDate: Timestamp.fromDate(nextRenewal),
      isActive: true,
      createdAt: serverTimestamp()
    });
  };

  const updateSubscription = async (subId, subData) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = subscriptions.map(s => {
        if (s.id === subId) {
          const startDate = subData.startDate ? new Date(subData.startDate) : s.startDate;
          const billingCycle = subData.billingCycle || s.billingCycle;
          return {
            ...s,
            ...subData,
            startDate,
            nextRenewalDate: calculateNextRenewal(startDate, billingCycle),
            updatedAt: new Date()
          };
        }
        return s;
      });
      setSubscriptions(updated);
      saveDemoSubs(updated);
      return;
    }

    const subRef = doc(db, 'users', currentUser.uid, 'subscriptions', subId);
    const updateData = { ...subData };
    if (subData.startDate) {
      updateData.startDate = Timestamp.fromDate(new Date(subData.startDate));
      updateData.nextRenewalDate = Timestamp.fromDate(
        calculateNextRenewal(subData.startDate, subData.billingCycle)
      );
    }
    await updateDoc(subRef, { ...updateData, updatedAt: serverTimestamp() });
  };

  const deleteSubscription = async (subId) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = subscriptions.filter(s => s.id !== subId);
      setSubscriptions(updated);
      saveDemoSubs(updated);
      return;
    }

    const subRef = doc(db, 'users', currentUser.uid, 'subscriptions', subId);
    await deleteDoc(subRef);
  };

  const cancelSubscription = async (subId) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = subscriptions.map(s => {
        if (s.id === subId) {
          return { ...s, isActive: false, cancelledAt: new Date() };
        }
        return s;
      });
      setSubscriptions(updated);
      saveDemoSubs(updated);
      return;
    }

    const subRef = doc(db, 'users', currentUser.uid, 'subscriptions', subId);
    await updateDoc(subRef, { isActive: false, cancelledAt: serverTimestamp() });
  };

  const reactivateSubscription = async (subId) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const sub = subscriptions.find(s => s.id === subId);
      const nextRenewal = calculateNextRenewal(sub?.startDate || new Date(), sub?.billingCycle || 'monthly');
      const updated = subscriptions.map(s => {
        if (s.id === subId) {
          return { ...s, isActive: true, nextRenewalDate: nextRenewal, cancelledAt: null };
        }
        return s;
      });
      setSubscriptions(updated);
      saveDemoSubs(updated);
      return;
    }

    const sub = subscriptions.find(s => s.id === subId);
    const subRef = doc(db, 'users', currentUser.uid, 'subscriptions', subId);
    const nextRenewal = calculateNextRenewal(sub.startDate, sub.billingCycle);
    await updateDoc(subRef, {
      isActive: true,
      nextRenewalDate: Timestamp.fromDate(nextRenewal),
      cancelledAt: null
    });
  };

  const getMonthlyTotal = () => {
    return subscriptions.filter(s => s.isActive).reduce((total, sub) => {
      if (sub.billingCycle === 'monthly') return total + sub.amount;
      if (sub.billingCycle === 'yearly') return total + (sub.amount / 12);
      if (sub.billingCycle === 'weekly') return total + (sub.amount * 4.33);
      return total;
    }, 0);
  };

  const getYearlyTotal = () => {
    return subscriptions.filter(s => s.isActive).reduce((total, sub) => {
      if (sub.billingCycle === 'yearly') return total + sub.amount;
      if (sub.billingCycle === 'monthly') return total + (sub.amount * 12);
      if (sub.billingCycle === 'weekly') return total + (sub.amount * 52);
      return total;
    }, 0);
  };

  const value = {
    subscriptions,
    loading,
    addSubscription,
    updateSubscription,
    deleteSubscription,
    cancelSubscription,
    reactivateSubscription,
    getMonthlyTotal,
    getYearlyTotal
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
